// HomePageStep.tsx
import { Separator } from "@/shadcnComponents/ui/separator";

import { Button } from "@/shadcnComponents/ui/button";
import { useEffect, useState } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../../shadcnComponents/ui/popover";
import {
  ArrowRight,
  Check,
  ChevronDown,
  ChevronsUpDown,
  SearchIcon,
} from "lucide-react";
import { Input } from "../../../../../shadcnComponents/ui/input";
import { useStaticData } from "../../../../../Providers/Data";
import React from "react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "../../../../../shadcnComponents/ui/command";
import { cn } from "../../../../../lib/utils";
import { useToast } from "../../../../../shadcnComponents/ui/use-toast";

export const HomePageStep = ({
  onSubmit,
  data,
  setData,
}: {
  onSubmit: () => void;
  data: any;
  setData: (any) => void;
}) => {
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let sanitizedValue: number | string = value;

    if (
      [
        "minImpression",
        "maxImpression",
        "minFollower",
        "maxFollower",
      ]?.includes(name)
    ) {
      sanitizedValue = parseInt(value, 10);
    }
    setData({
      ...data,
      [name]: sanitizedValue,
    });
  };

  //master data
  const staticDataState = useStaticData();
  const [masterContentCategory, setMasterContentCategory] = useState([]);

  useEffect(() => {
    staticDataState?.getNewDataFromType("Content Type").then((e) => {
      setMasterContentCategory(e);
    });
  }, [staticDataState]);

  return (
    <>
      <div className="flex items-center flex-col gap-6">
        <div className="inline-flex items-center flex-wrap w-full justify-center gap-[18px] border px-6 py-3 rounded-lg border-solid border-[#4F4F4F] mt-20">
          <NicheCategorySelect
            masterContentCategory={masterContentCategory}
            selectionLimit={3}
            onChange={(value) => {
              setData({
                ...data,
                NicheCategory: value,
              });
            }}
          />

          <Separator orientation="vertical" className="h-5 bg-[#333] " />

          <Input
            value={data?.minFollower > 0 ? data?.minFollower : ""}
            name="minFollower"
            type="number"
            placeholder="No. of Followers"
            className="w-[180px] bg-transparent border-none text-[#BFBFBF] font-[Inter] text-sm not-italic font-normal leading-[24px] placeholder:text-[#BFBFBF] placeholder:text-sm"
            onChange={handleInputChange}
          />

          <Separator orientation="vertical" className="h-5 bg-[#333] " />

          <AvgImpressionsSelect
            data={data}
            handleInputChange={handleInputChange}
          />
        </div>
        <Button
          variant="destructive"
          className="bg-[#DC2627] gap-2.5 px-12"
          onClick={onSubmit}
        >
          Calculate
          <ArrowRight size={16} />
        </Button>
      </div>
    </>
  );
};

const AvgImpressionsSelect = ({
  data,
  handleInputChange,
}: {
  data: {
    minImpression: number;
    maxImpression: number;
  };
  handleInputChange: (e?: React.ChangeEvent<HTMLInputElement>) => void;
}) => {
  return (
    <Popover>
      <PopoverTrigger className="w-[180px]  flex justify-between items-center self-stretch px-4 py-2 rounded-md bg-transparent border-none focus:ring-offset-0">
        <div className="flex justify-between gap-2 items-center w-full">
          <div className="text-[#BFBFBF] font-[Inter] text-sm not-italic font-normal leading-[24px] line-clamp-1 text-start">
            {data?.minImpression > 0 && data?.maxImpression > 0
              ? `${data?.minImpression} - ${data?.maxImpression}`
              : "Avg. Impressions"}
          </div>
          <ChevronDown size={16} />
        </div>
      </PopoverTrigger>
      <PopoverContent className="bg-[#171717] border border-[#919191] text-[#919191] p-3 flex flex-col gap-3">
        <p className="text-[#919191] text-sm not-italic font-medium leading-[20px]">
          Enter a range (per post)
        </p>
        <div className="flex gap-3 justify-between items-center">
          <Input
            value={data?.minImpression > 0 ? data?.minImpression : ""}
            name="minImpression"
            type="number"
            placeholder="Min"
            className="bg-transparent text-[#919191] border border-[#666666]"
            onChange={handleInputChange}
          />
          <Input
            value={data?.maxImpression > 0 ? data?.maxImpression : ""}
            name="maxImpression"
            type="number"
            placeholder="Max"
            className="bg-transparent text-[#919191] border border-[#666666]"
            onChange={handleInputChange}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

const NicheCategorySelect = ({
  masterContentCategory,
  selectionLimit = 0,
  onChange,
}: {
  masterContentCategory?: {
    _id?: string;
    categoryName?: string;
  }[];
  selectionLimit?: number;
  onChange?: (value: string[]) => void;
}) => {
  const { toast } = useToast();
  const [data, setData] = useState<
    {
      value: string;
      label: string;
    }[]
  >();
  const [filteredData, setFilteredData] = useState(data);

  const [open, setOpen] = React.useState(false);
  const [value, setValue] = React.useState([]);
  const [searchInput, setSearchInput] = useState("");

  useEffect(() => {
    if (masterContentCategory?.length > 0) {
      const mData = masterContentCategory.map((item) => ({
        value: item?._id,
        label: item?.categoryName,
      }));
      setData(mData);
    }
  }, [masterContentCategory]);

  useEffect(() => {
    if (searchInput) {
      setFilteredData(
        data.filter((category) =>
          category?.label?.toLowerCase()?.includes(searchInput?.toLowerCase())
        )
      );
    } else {
      setFilteredData(data);
    }
  }, [searchInput, data]);

  useEffect(() => {
    if (value && onChange) {
      onChange(value);
    }
  }, [value]);

  const handleSelectMultiple = (selectedValue: string) => {
    setValue((prev) => {
      if (prev.includes(selectedValue)) {
        // console.log("includes prev", prev, "selectedValue", selectedValue);
        return prev.filter((item) => item !== selectedValue);
      }
      if (selectionLimit !== 0 && prev.length >= selectionLimit) {
        toast({
          title: `Maximum of ${selectionLimit} categories can be selected.`,
          variant: "destructive",
        });
        return prev;
      }
      // console.log("prev", prev, "selectedValue", selectedValue);
      return [...prev, selectedValue];
    });
    setSearchInput("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          role="combobox"
          aria-expanded={open}
          className="w-[180px] flex justify-between items-center self-stretch px-4 py-2 rounded-md bg-transparent border-none focus:ring-offset-0 gap-3"
        >
          <span className="text-[#BFBFBF] font-[Inter] text-sm not-italic font-normal leading-[24px] line-clamp-1 text-start">
            {value?.length > 0 ? `Selected: ${value?.length}` : "Content Niche"}
          </span>

          <ChevronsUpDown className="opacity-50" size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-0 bg-[#171717] text-[#919191] ">
        <Command className="w-[200px] p-0 bg-[#171717] border border-[#919191] text-[#919191] ">
          <div className="relative flex items-center">
            <SearchIcon
              className="absolute left-3 text-muted-foreground"
              size={16}
            />
            <Input
              placeholder="Search category..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-11 w-full pl-10 rounded-md bg-transparent py-3 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 border border-muted-foreground"
            />
          </div>
          <CommandList
            style={{ scrollbarWidth: "thin" }}
            className="w-[200px] p-0 bg-[#171717] text-[#919191] "
          >
            <CommandEmpty>No framework found.</CommandEmpty>
            <CommandGroup>
              {filteredData?.map((category) => (
                <CommandItem
                  key={category?.value}
                  value={category?.value}
                  onSelect={(currentValue) => {
                    handleSelectMultiple(currentValue);
                  }}
                  className="text-[#919191]"
                >
                  {category?.label}
                  <Check
                    className={cn(
                      "ml-auto",
                      value?.find((v) => v === category?.value)
                        ? "opacity-100"
                        : "opacity-0"
                    )}
                    size={16}
                  />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
